/**
 * Binds a loaded model to the contract's names (bramContract.ts): its bones, shape keys and clips. The real model
 * uses the exact names; anything else (the troop-soldier stand-in, a re-export with an armature prefix) is matched
 * through aliases, and whatever is still missing is left out so the animator falls back to procedural motion.
 */
import * as THREE from "three";
import { BRAM_BONES, BRAM_CLIPS, BRAM_MORPHS, type BramBone, type BramClip, type BramMorph } from "./bramContract";

/**
 * The stand-in's parts under the contract's names. GLTFLoader strips `.` from node names ("troop_soldier.head" is
 * "troop_soldierhead"), so matching ignores punctuation and case.
 */
const BONE_ALIASES: Partial<Record<BramBone, readonly string[]>> = {
  root: ["troop_soldier"],
  chest: ["troop_soldier.body"],
  spine: ["troop_soldier.body"],
  head: ["troop_soldier.head"],
  upperarm_L: ["troop_soldier.arm_l"],
  upperarm_R: ["troop_soldier.arm_r"],
};
/**
 * A missing clip borrows another before it goes procedural: a model with no `talk` talks over its idle; a pass-1
 * model (no `laugh`, `thumbs_up`) cheers or nods instead.
 */
const CLIP_FALLBACK: Partial<Record<BramClip, BramClip>> = { talk: "idle", laugh: "cheer", thumbs_up: "nod" };

const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/** The node name standing for each contract bone, or null. Pure: tests feed it name lists. */
export function resolveBones(nodeNames: readonly string[]): Record<BramBone, string | null> {
  const byKey = new Map<string, string>();
  for (const n of nodeNames) if (!byKey.has(squash(n))) byKey.set(squash(n), n);
  const out = {} as Record<BramBone, string | null>;
  for (const bone of BRAM_BONES) {
    let hit = byKey.get(squash(bone)) ?? null;
    for (const alias of BONE_ALIASES[bone] ?? []) hit ??= byKey.get(squash(alias)) ?? null;
    // an armature prefix ("Armature_head", "mixamorig:Head"): the name ends in the bone after a separator
    if (!hit) {
      const tail = new RegExp(`(^|[^a-z0-9])${bone.toLowerCase().replace(/[^a-z0-9]/g, "[^a-z0-9]?")}$`);
      hit = nodeNames.find((n) => tail.test(n.toLowerCase())) ?? null;
    }
    out[bone] = hit;
  }
  return out;
}

/** The clip name playing each contract clip (exact, `Armature|idle`, any case), borrowing per CLIP_FALLBACK, or null. */
export function resolveClips(clipNames: readonly string[]): Record<BramClip, string | null> {
  const find = (c: string) => clipNames.find((n) => n === c) ?? clipNames.find((n) => n.toLowerCase() === c || n.toLowerCase().endsWith(`|${c}`)) ?? null;
  const out = {} as Record<BramClip, string | null>;
  for (const c of BRAM_CLIPS) out[c] = find(c);
  for (const c of BRAM_CLIPS) { const alt = CLIP_FALLBACK[c]; if (!out[c] && alt) out[c] = out[alt]; }
  return out;
}

export interface MorphSlot { readonly mesh: THREE.Mesh; readonly index: number }

export interface BramRig {
  readonly scene: THREE.Object3D;
  readonly bones: Partial<Record<BramBone, THREE.Object3D>>;
  readonly clips: Partial<Record<BramClip, THREE.AnimationClip>>;
  /** Every mesh slot of each shape key (a head split into primitives has several). */
  readonly morphs: Partial<Record<BramMorph, readonly MorphSlot[]>>;
  /** Not the contract model: framing and motion lean on fallbacks. */
  readonly standIn: boolean;
}

export function bindRig(scene: THREE.Object3D, animations: readonly THREE.AnimationClip[]): BramRig {
  const names: string[] = [];
  scene.traverse((o) => { if (o.name) names.push(o.name); });
  const boneNames = resolveBones(names);
  const bones: Partial<Record<BramBone, THREE.Object3D>> = {};
  for (const b of BRAM_BONES) { const n = boneNames[b]; const o = n ? scene.getObjectByName(n) : undefined; if (o) bones[b] = o; }
  const clipNames = resolveClips(animations.map((a) => a.name));
  const clips: Partial<Record<BramClip, THREE.AnimationClip>> = {};
  for (const c of BRAM_CLIPS) { const n = clipNames[c]; const a = n ? animations.find((x) => x.name === n) : undefined; if (a) clips[c] = a; }
  const morphs: Partial<Record<BramMorph, MorphSlot[]>> = {};
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    const dict = mesh.isMesh ? mesh.morphTargetDictionary : undefined;
    if (!dict || !mesh.morphTargetInfluences) return;
    for (const m of BRAM_MORPHS) {
      const key = Object.keys(dict).find((k) => k === m) ?? Object.keys(dict).find((k) => squash(k) === squash(m));
      if (key !== undefined) (morphs[m] ??= []).push({ mesh, index: dict[key] });
    }
  });
  return { scene, bones, clips, morphs, standIn: scene.getObjectByName("head") === undefined };
}

/** Seconds of each contract clip in this rig (the contract's number when the rig lacks it). */
export function clipSeconds(rig: BramRig, fallback: Readonly<Record<BramClip, number>>): (c: BramClip) => number {
  return (c) => rig.clips[c]?.duration ?? fallback[c];
}
